import { ref } from 'vue';
import { usePreferences } from './utils/usePreferences';

// Portal text size, picked in the App Settings card on the Profile page.
//
// It is kept with the other portal preferences (the "readyChek" storage entry), which
// sign-in and sign-out merge into rather than replace, so the next sign-in on this browser
// opens at the same size.
//
// It works by setting the root font size. Vuetify sizes its type in rem, so every page
// follows without any page knowing about it, while menus, dialogs and maps keep measuring
// in real pixels. CSS `zoom` would scale the whole screen instead, but it throws off the
// positioning of Vuetify's menus and of clicks on the Google map, so it is not used.
const PREFERENCE_PAGE = 'app';
const PREFERENCE_FIELD = 'textSize';

const { pagePreference, savePagePreference } = usePreferences();

// Percent of the browser's own font size, so a manager who has already enlarged text in
// the browser keeps that and scales from it. Each step has a shell_text_size_* label so the menu reads in words, not just numbers.
export const TEXT_SIZE_STEPS = [87.5, 100, 112.5, 125, 137.5, 150];
export const TEXT_SIZE_LABEL_KEYS = {
  87.5: 'shell_text_size_small',
  100: 'shell_text_size_normal',
  112.5: 'shell_text_size_large',
  125: 'shell_text_size_larger',
  137.5: 'shell_text_size_extra_large',
  150: 'shell_text_size_largest',
};
const DEFAULT_SIZE = 100;

const readSaved = () => {
  const value = parseFloat(pagePreference(PREFERENCE_FIELD, DEFAULT_SIZE, PREFERENCE_PAGE));
  return TEXT_SIZE_STEPS.includes(value) ? value : DEFAULT_SIZE;
};

const textSize = ref(readSaved());

const apply = () => {
  textSize.value = readSaved();
  document.documentElement.style.fontSize = textSize.value === DEFAULT_SIZE ? '' : `${textSize.value}%`;
};

const save = (value) => {
  savePagePreference(PREFERENCE_FIELD, value, PREFERENCE_PAGE);
  apply();
};

export function useTextSize() {
  return {
    textSize,
    textSizeOptions: TEXT_SIZE_STEPS,
    textSizeLabelKey: (value) => TEXT_SIZE_LABEL_KEYS[value],
    applyTextSize: apply,
    setTextSize: (value) => { if (TEXT_SIZE_STEPS.includes(value)) save(value); },
  };
}
